import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import reverse from 'lodash/reverse';
import flatten from 'lodash/flatten';
import { BaseEntryProductComponent } from '@products/base-entry-product-component';
import {
  FundDocumentCategoryDTO,
  FundDocumentsService,
} from './services/fund-documents.service';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk/';
import { Subscription } from 'rxjs';
import { Logger } from '@utils/logger';
import { LiteratureDTO, ProductDetailConfiguration } from '@types';
import { FundDocument } from '@components/products/types/fund-document.type';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import cloneDeep from 'lodash/cloneDeep';
import {
  OtherFundDetailsDisplayComponent
} from "@products/other-details/other-fund-details-display-component.interface";
import { OtherFundDetailsComponentConfig } from "@products/other-details/other-fund-details-component.config";
import { BaseProductDetailComponent } from "@products/base-product-detail.component";

const logger = Logger.getLogger('Fund Documents');

@Component({
  selector: 'ft-fund-documents',
  templateUrl: './fund-documents.component.html',
  styleUrls: ['./fund-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundDocumentsComponent
  extends BaseProductDetailComponent
  implements OtherFundDetailsDisplayComponent, AfterViewInit, OnDestroy {
  public fundDocumentCategory: FundDocumentCategoryDTO;
  public fundDocuments: any;
  public errorInService = false;
  public docsLoaded = false;
  subscription: Subscription;
  fundVideo: SafeResourceUrl;
  fundVideoImage: SafeResourceUrl;
  popvisible = false;
  hashNavigation: any;
  monthlyFromDate: string;
  monthlyToDate: string;
  hyearlyFromDate: string;
  hyearlyToDate: string;
  fortnightlyFromDate: string;
  fortnightlyToDate: string;
  filteredFundDocumentsMonthly: any[] = [];
  filteredFundDocumentsHalfyearly: any[] = [];
  filteredFundDocumentsFortnightly: any[] = [];
  @ViewChild('filteredContentMon') filteredContentMon: TemplateRef<any>;
  @ViewChild('filteredContentYearly') filteredContentYearly: TemplateRef<any>;
  @ViewChild('filteredContentFortnightly') filteredContentFortnightly: TemplateRef<any>;

  constructor(
    private fundService: FundDocumentsService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {
    super();
    this.hashNavigation = this.route.snapshot?.fragment;
  }

  populate(
    componentConfig: OtherFundDetailsComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.initialize(brConfig, component, page);
    const customFundCategory = {
      name: 'india literature fund documents tab',
      fundDocTypeToDisplay_csv: '',
      fundDocCategory: 'FUND-DOCUMENTS',
      documents: [],
    };

    // Get Fund Types (other than categories)
    const fundType = this.fundService.fundLiterature?.fundType[0]?.documentTypeDisplay_csv?.split(
      ','
    );
    // Set Literature Categories
    this.fundDocumentCategory = cloneDeep(
      this.fundService.fundLiterature?.fundCategory
    );
    // Call FundService to pull the data
    this.subscription = this.fundService
      .getFundLiteratureDocument$(
        '?channel=en-in&cache=true&frkOneTISNumber=' + this.brConfig?.fundId
      )
      ?.subscribe(
        (data: LiteratureDTO): void => {
          const fundTypeDocs = [];
          this.docsLoaded = true;

          // Get data for Fund Category
          forEach(this.fundDocumentCategory, (input) => {
            const fundDoc = filter(data.document, {
              contentGrouping: input.fundDocCategory,
            });
            if (fundDoc.length > 0) {
              // Check if file extension is present, otherwise add one from file url
              fundDoc.map((doc) => {
                doc.fileType = doc.fileExtension
                  ? doc.fileExtension
                  : this.fileExtension(doc);
              });

              input.documents = fundDoc;
              logger.debug(input);
            }
          });
          this.fundDocuments = this.fundDocumentCategory;
          // Get data for Fund Type
          forEach(fundType, (sortedCategories) => {
            const fundDoc = filter(data.document, {
              contentGrouping: sortedCategories,
            });
            if (fundDoc.length > 0) {
              // Check if file extension is present, otherwise add one from file url
              fundDoc.map((doc) => {
                doc.fileType = doc.fileExtension
                  ? doc.fileExtension
                  : this.fileExtension(doc);
              });
              fundTypeDocs.push(fundDoc);
            }
          });

          // Match the document sequence per production
          customFundCategory.documents = flatten(fundTypeDocs);

          /**
           * Push custom object in 'Fund Category' with 'Fund Documents'
           */
          this.fundDocuments.push(reverse(customFundCategory));
          this.changeDetectorRef.detectChanges();
        },
        (err) => {
          logger.debug('Literature HTTP Error', err);
          this.errorInService = true;
          this.changeDetectorRef.detectChanges();
        }
      );
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit() {
    if (this.hashNavigation !== undefined) {
      setTimeout(() => {
        this.smoothScroll(this.hashNavigation);
      }, 2000);
    }
    this.route.queryParams?.subscribe((queryParams) => {
      const scrollToDocs = queryParams.scrolltodocs;
      if (scrollToDocs) {
        this.scrollToDocs();
      }
    })
  }

  private scrollToDocs() {
    const elementToScroll = document.getElementById('fund-docs-section');
    if (elementToScroll) {
      setTimeout(() => {
        elementToScroll.scrollIntoView({behavior: "smooth", block: "start"});
      }, 3000);
    }
  }

  smoothScroll(target) {
    let stra2 = document.querySelector('#' + target);
    stra2 = stra2 === undefined ? document.querySelector(`#${target}`) : stra2;
    const doScrolling = (elementY, duration) => {
      const startingY = window.pageYOffset;
      const diff = elementY - startingY;
      let start;

      // Bootstrap our animation - it will get called right before next frame shall be rendered.
      window.requestAnimationFrame(function step(timestamp) {
        if (!start) {
          start = timestamp;
        }
        // Elapsed milliseconds since start of scrolling.
        const time = timestamp - start;
        // Get percent of completion in range [0, 1].
        const percent = Math.min(time / duration, 1);

        window.scrollTo(0, startingY + diff * percent + 30);

        // Proceed with animation as long as we wanted it to.
        if (time < duration) {
          window.requestAnimationFrame(step);
        }
      });
    };

    if (stra2 != null) {
      doScrolling(stra2.getBoundingClientRect().top + window.scrollY - 40, 10);
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  // Capture file extension from document url (if filetype is not present)
  public fileExtension(input) {
    const extensionIndex = input?.literatureHref.lastIndexOf('.');
    return input?.literatureHref?.slice(extensionIndex + 1).toLowerCase();
  }

  downloadDocument(document) {
    window.open('download' + document.literatureHref, '_blank');
  }

  hasDocuments(category: string): boolean {
    const categoryData = this.fundDocuments.find(item => item.fundDocCategory === category);
    return categoryData && categoryData.documents && categoryData.documents.length > 0;
  }

  fiterDocuments(category: string) {
    let fromDate: Date;
    let toDate: Date;
    if (category === 'Dis-Portfolio-Monthly' && this.monthlyFromDate && this.monthlyToDate) {
      fromDate = this.parseMonthYearStringFromDate(this.monthlyFromDate);
      toDate = this.parseMonthYearStringToDate(this.monthlyToDate);
    } else if (category === 'Dis-Portfolio-Halfyearly' && this.hyearlyFromDate && this.hyearlyToDate) {
      fromDate = this.parseMonthYearStringFromDate(this.hyearlyFromDate);
      toDate = this.parseMonthYearStringToDate(this.hyearlyToDate);
    } else if (category === 'Dis-Portfolio-Fortnightly' && this.fortnightlyFromDate && this.fortnightlyToDate) {
      fromDate = this.parseMonthYearStringFromDate(this.fortnightlyFromDate);
      toDate = this.parseMonthYearStringToDate(this.fortnightlyToDate);
    } else {
      return
    }

    if (this.fundDocuments) {
      const filteredDocuments = this.fundDocuments
        .filter((documentCategory) => documentCategory.fundDocCategory === category)
        .map((documentCategory) => {
          return {
            ...documentCategory,
            documents: documentCategory.documents.filter((document) => {
              const referenceDate = new Date(document.frkReferenceDate);
              return referenceDate >= fromDate && referenceDate <= toDate;
            }),
          };
        });
      if (category === 'Dis-Portfolio-Monthly') {
        this.filteredFundDocumentsMonthly = filteredDocuments;
      } else if (category === 'Dis-Portfolio-Halfyearly') {
        this.filteredFundDocumentsHalfyearly = filteredDocuments;
      } else if (category === 'Dis-Portfolio-Fortnightly') {
        this.filteredFundDocumentsFortnightly = filteredDocuments;
      }
    }
  }

  parseMonthYearStringFromDate(monthYearString: string): Date {
    const [month, year] = monthYearString.split(' ');
    return new Date(`${month} 1, ${year}`);
  }

  parseMonthYearStringToDate(monthYearString: string): Date {
    const [month, year] = monthYearString.split(' ');
    const lastDayOfMonth = new Date(new Date(`${month} 1, ${year}`));
    lastDayOfMonth.setMonth(lastDayOfMonth.getMonth() + 1);
    lastDayOfMonth.setDate(lastDayOfMonth.getDate() - 1);
    return lastDayOfMonth;
  }

  onFinalDate(selectedDate, dateField, tab) {
    if (tab === 'monthly') {
      if (dateField === 'fromDate' && this.monthlyFromDate !== selectedDate) {
        this.monthlyFromDate = selectedDate;
      } else if (dateField === 'toDate' && this.monthlyToDate !== selectedDate) {
        this.monthlyToDate = selectedDate;
      }
    } else if (tab === 'hyearly') {
      if (dateField === 'fromDate' && this.hyearlyFromDate !== selectedDate) {
        this.hyearlyFromDate = selectedDate;
      } else if (dateField === 'toDate' && this.hyearlyToDate !== selectedDate) {
        this.hyearlyToDate = selectedDate;
      }
    } else if (tab === 'fortnightly') {
      if (dateField === 'fromDate' && this.fortnightlyFromDate !== selectedDate) {
        this.fortnightlyFromDate = selectedDate;
      } else if (dateField === 'toDate' && this.fortnightlyToDate !== selectedDate) {
        this.fortnightlyToDate = selectedDate;
      }
    }
  }
}
